import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { usePageTitle } from 'utils/pageTitle';
import Spinner from 'components/Spinner';
import { openModal } from 'data/reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import { useDatasetMonitor } from 'data/repositories/datasetMonitorRepository';
import AdminLayout from '../AdminLayout';
import DatasetTable, { sortGroupNames } from './components/DatasetTable';
import DatasetHealthSummary from './components/DatasetHealthSummary';
import { ALL_HEALTH_VALUES, HEALTH_LABEL } from './components/HealthChip';
import './DatasetMonitor.css';

/**
 * DatasetMonitor
 *
 * Admin page showing, per dataset, when data last landed, how much landed,
 * whether we emailed about it, and whether a gap is a real failure or
 * deliberate suppression (design spec 1). This container owns URL state,
 * the fetch, the refresh toggle and the group/health filters; the table,
 * row and drawer are extracted components (design spec 12) - unlike
 * QueueMonitor.jsx (2,279 lines), this file stays close to 250.
 */

// Reporting window options (hours) sent to GET /dataset-monitor/datasets.
// Not specified as a fixed contract list (unlike Queue Monitor's failed-job
// window), so this mirrors that same pattern with a reasonable day/week set.
const WINDOW_HOURS_OPTIONS = [24, 72, 168];
const DEFAULT_WINDOW_HOURS = 24;

// Ingest runs every four hours, daily or weekly - not Queue Monitor's 30s
// queue-depth cadence. 30s polling here would be ~480 requests per ingest
// cycle for data that changes six times a day, so auto-refresh is opt-in
// and slow: 5 minutes, default off.
const AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// ============================================
// Deep-linkable URL state (?dataset=&group=&health=&hours=), mirroring
// QueueMonitor.jsx's parseUrlState/buildUrlSearch: defaults are OMITTED
// from the URL, invalid values fall back SILENTLY, and length caps mirror
// the backend validation rules.
// ============================================
const MAX_DATASET_PARAM_LENGTH = 64;
const MAX_GROUP_PARAM_LENGTH = 32;

const parseUrlState = (search) => {
	const params = new URLSearchParams(search || '');
	const dataset = params.get('dataset');
	const group = params.get('group');
	const health = params.get('health');
	const hours = parseInt(params.get('hours'), 10);
	return {
		dataset:
			dataset && dataset.length <= MAX_DATASET_PARAM_LENGTH ? dataset : null,
		group: group && group.length <= MAX_GROUP_PARAM_LENGTH ? group : null,
		health: ALL_HEALTH_VALUES.includes(health) ? health : null,
		hours: WINDOW_HOURS_OPTIONS.includes(hours) ? hours : DEFAULT_WINDOW_HOURS,
	};
};

const buildUrlSearch = ({ dataset, group, health, hours }) => {
	const params = new URLSearchParams();
	if (dataset) params.set('dataset', dataset);
	if (group) params.set('group', group);
	if (health) params.set('health', health);
	if (hours && hours !== DEFAULT_WINDOW_HOURS) params.set('hours', String(hours));
	const search = params.toString();
	return search ? `?${search}` : '';
};

const DatasetMonitor = () => {
	usePageTitle('Admin | Dataset Monitor');
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();

	// URL state is read ONCE on mount; invalid params fall back silently to
	// defaults. URL writes happen only inside user-event handlers via
	// history.replace - auto-refresh cycles never touch the URL.
	const [initialUrlState] = useState(() => parseUrlState(location.search));

	const [selectedDataset, setSelectedDataset] = useState(initialUrlState.dataset);
	const [selectedGroup, setSelectedGroup] = useState(initialUrlState.group);
	const [selectedHealth, setSelectedHealth] = useState(initialUrlState.health);
	const [hours, setHours] = useState(initialUrlState.hours);
	const [autoRefresh, setAutoRefresh] = useState(false);

	// Tracks whether OUR drawer (not some unrelated modal) is currently open,
	// so its close can be detected regardless of how it closed (its own
	// Close button, an outside click, or Esc all just dispatch closeModal()).
	const isDrawerOpen = useSelector(
		(state) =>
			state.modal.isOpen &&
			state.modal.currentModal === MODAL_TYPES.DATASET_DRAWER,
	);

	const {
		actions,
		datasets: datasetsResponse,
		isLoadingDatasets,
	} = useDatasetMonitor();

	// getDatasets returns a custom envelope (generated_at/window_hours/
	// coverage/datasets), not a Laravel paginator, so createRepository does
	// not unwrap it - the hook's `datasets` field is the whole envelope.
	const allDatasets = useMemo(
		() => datasetsResponse?.datasets || [],
		[datasetsResponse],
	);
	const coverage = datasetsResponse?.coverage;

	// Set by the repository when the request itself failed (see its `error`
	// envelope). Distinct from "the API answered with no rows": without this the
	// page shows an empty table for a 404, a 500, a 504 and an expired session
	// alike, which is indistinguishable from a quiet pipeline.
	const loadError = datasetsResponse?.error;

	const syncUrlState = useCallback(
		(overrides = {}) => {
			history.replace({
				search: buildUrlSearch({
					dataset: selectedDataset,
					group: selectedGroup,
					health: selectedHealth,
					hours,
					...overrides,
				}),
			});
		},
		[history, selectedDataset, selectedGroup, selectedHealth, hours],
	);

	// Initial load + refetch whenever the reporting window changes.
	useEffect(() => {
		actions.getDatasets({ hours });
	}, [actions, hours]);

	// A dataset deep-linked in the URL (e.g. from an alert email) opens its
	// drawer once, on mount, carrying the same window the table itself opens
	// with (hours already equals initialUrlState.hours at this first render).
	useEffect(() => {
		if (initialUrlState.dataset) {
			dispatch(
				openModal(MODAL_TYPES.DATASET_DRAWER, {
					datasetKey: initialUrlState.dataset,
					hours,
				}),
			);
		}
		// Runs once on mount only - initialUrlState is a stable ref.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Clears ?dataset= the moment the drawer closes, however it closed.
	// Guarded by a true->false transition (not just "!isDrawerOpen") so this
	// cannot fire on initial mount before the deep-link effect above has had
	// a chance to open the drawer: isDrawerOpen and wasDrawerOpenRef.current
	// both start false, so the guard is false until a real open is observed
	// first via handleSelectDataset or the deep-link effect's dispatch.
	const wasDrawerOpenRef = useRef(false);
	useEffect(() => {
		if (wasDrawerOpenRef.current && !isDrawerOpen) {
			setSelectedDataset(null);
			syncUrlState({ dataset: null });
		}
		wasDrawerOpenRef.current = isDrawerOpen;
	}, [isDrawerOpen, syncUrlState]);

	// Review finding: the drawer's stat card used to fetch an independent,
	// window-less request, so it kept reading "Coverage 24h" even after the
	// table's own window (and its "Coverage <hours>h" header) moved to 168h.
	// Re-dispatching openModal with the SAME datasetKey but the new `hours`
	// only replaces modalOptions in the Redux store - isOpen/currentModal are
	// unchanged, so ModalManager keeps the same DatasetDrawer instance
	// mounted and it simply re-renders with a new `options.hours`, which its
	// own effect (DatasetDrawer.jsx) uses to refetch. Guarded on
	// selectedDataset (not just isDrawerOpen) so opening the drawer for the
	// first time stays handleSelectDataset's/the deep-link effect's job -
	// this effect exists solely to react to the window changing while a
	// drawer is already open.
	useEffect(() => {
		if (isDrawerOpen && selectedDataset) {
			dispatch(
				openModal(MODAL_TYPES.DATASET_DRAWER, {
					datasetKey: selectedDataset,
					hours,
				}),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hours]);

	// Opt-in auto-refresh (5 minutes).
	useEffect(() => {
		if (!autoRefresh) {
			return undefined;
		}
		const interval = setInterval(() => {
			actions.getDatasets({ hours });
		}, AUTO_REFRESH_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [autoRefresh, actions, hours]);

	// Group/health filter options come from the fetched datasets themselves
	// (never a hardcoded list, matching QueueMonitor's queueOptions
	// convention) so a deep-linked value missing from the payload stays
	// selectable, and a backend-renamed group cannot silently disappear.
	const groupOptions = useMemo(() => {
		const groups = Array.from(
			new Set(allDatasets.map((dataset) => dataset.group).filter(Boolean)),
		);
		if (selectedGroup && !groups.includes(selectedGroup)) {
			groups.push(selectedGroup);
		}
		return sortGroupNames(groups);
	}, [allDatasets, selectedGroup]);

	const healthOptions = useMemo(() => {
		const healths = ALL_HEALTH_VALUES.filter((health) =>
			allDatasets.some((dataset) => dataset.health === health),
		);
		if (selectedHealth && !healths.includes(selectedHealth)) {
			healths.push(selectedHealth);
		}
		return healths;
	}, [allDatasets, selectedHealth]);

	const filteredDatasets = useMemo(
		() =>
			allDatasets.filter((dataset) => {
				if (selectedGroup && dataset.group !== selectedGroup) return false;
				if (selectedHealth && dataset.health !== selectedHealth) return false;
				return true;
			}),
		[allDatasets, selectedGroup, selectedHealth],
	);

	const handleSelectDataset = useCallback(
		(key) => {
			setSelectedDataset(key);
			dispatch(
				openModal(MODAL_TYPES.DATASET_DRAWER, { datasetKey: key, hours }),
			);
			syncUrlState({ dataset: key });
		},
		[dispatch, syncUrlState, hours],
	);

	const handleGroupChange = useCallback(
		(event) => {
			const value = event.target.value || null;
			setSelectedGroup(value);
			syncUrlState({ group: value });
		},
		[syncUrlState],
	);

	/**
	 * Apply a health filter from a raw value rather than a change event, so
	 * the summary tiles and the select can share one code path.
	 */
	const applyHealthFilter = useCallback(
		(value) => {
			const next = value || null;
			setSelectedHealth(next);
			syncUrlState({ health: next });
		},
		[syncUrlState],
	);

	const handleHealthChange = useCallback(
		(event) => {
			applyHealthFilter(event.target.value);
		},
		[applyHealthFilter],
	);

	const handleHoursChange = useCallback(
		(event) => {
			const value = parseInt(event.target.value, 10);
			if (!WINDOW_HOURS_OPTIONS.includes(value)) {
				return;
			}
			setHours(value);
			syncUrlState({ hours: value });
		},
		[syncUrlState],
	);

	const handleShowLegend = useCallback(() => {
		dispatch(openModal(MODAL_TYPES.DATASET_HEALTH_LEGEND));
	}, [dispatch]);

	const handleRefresh = useCallback(() => {
		actions.getDatasets({ hours });
	}, [actions, hours]);

	const handleAutoRefreshToggle = useCallback((event) => {
		setAutoRefresh(event.target.checked);
	}, []);

	if (isLoadingDatasets && !datasetsResponse) {
		return (
			<AdminLayout>
				<Spinner />
			</AdminLayout>
		);
	}

	return (
		<AdminLayout>
			<div className="dataset-monitor">
				<div className="dataset-monitor-header">
					<h1>Dataset Monitor</h1>
					<div className="dataset-monitor-controls">
						<select
							className="form-select form-select-sm"
							value={selectedGroup || ''}
							onChange={handleGroupChange}
							aria-label="Filter by group"
						>
							<option value="">All groups</option>
							{groupOptions.map((group) => (
								<option key={group} value={group}>
									{group}
								</option>
							))}
						</select>
						<select
							className="form-select form-select-sm"
							value={selectedHealth || ''}
							onChange={handleHealthChange}
							aria-label="Filter by health"
						>
							<option value="">All statuses</option>
							{healthOptions.map((health) => (
								<option key={health} value={health}>
									{HEALTH_LABEL[health] || health}
								</option>
							))}
						</select>
						<select
							className="form-select form-select-sm"
							value={hours}
							onChange={handleHoursChange}
							aria-label="Reporting window"
						>
							{WINDOW_HOURS_OPTIONS.map((option) => (
								<option key={option} value={option}>
									Last {option}h
								</option>
							))}
						</select>
						<label className="auto-refresh-toggle">
							<input
								type="checkbox"
								checked={autoRefresh}
								onChange={handleAutoRefreshToggle}
							/>
							<span className="ms-2">Auto-refresh (5m)</span>
						</label>
						<button
							onClick={handleRefresh}
							className="btn btn-sm btn-primary"
							disabled={isLoadingDatasets}
						>
							{isLoadingDatasets ? 'Refreshing...' : 'Refresh'}
						</button>
					</div>
				</div>

				{loadError && (
					<div className="alert alert-danger py-2" role="alert">
						<strong className="me-1">Could not load datasets.</strong>
						{loadError.status ? `HTTP ${loadError.status}. ` : ''}
						{loadError.message}
						<span className="d-block small text-muted mt-1">
							API: {loadError.apiUrl}
						</span>
					</div>
				)}

				{coverage?.note && (
					<div className="dataset-monitor-coverage-note text-muted small px-2 py-1 mb-3">
						<i className="bi bi-hourglass-split me-2" aria-hidden="true" />
						{coverage.note}
					</div>
				)}

				<DatasetHealthSummary
					datasets={allDatasets}
					selectedHealth={selectedHealth}
					onSelectHealth={applyHealthFilter}
					onShowLegend={handleShowLegend}
				/>

				<div className="dataset-monitor-card">
					<DatasetTable
						datasets={filteredDatasets}
						onSelectDataset={handleSelectDataset}
						hours={hours}
					/>
				</div>
			</div>
		</AdminLayout>
	);
};

export default DatasetMonitor;
